import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { useToast } from '../hooks/use-toast';
import { useLanguageNavigation } from '../components/routing/LanguageWrapper';
import { addMockReport, getReportById } from '../utils/mockReportsData';
import { buildReportTemplateDocument } from '../utils/reportTemplateGenerator';
import { getDossierByReportId } from '../utils/mockDossierData';
import { MOCK_STATIONS } from '../utils/mockStationsData';
import {
  getDraftStorageKey,
  getSerializedEditorState,
} from '../utils/reportDraftStorage';
import { loadBestDraft } from '../utils/reportDraftRepository';
import { getBackendEndpoint, getBackendOrigin } from '../utils/backendApiUrl';
import { sanitizeSerializedStateForOfficialExport } from '../utils/reportExportSanitizer';
import { ExportSettings } from '../components/reports/Preview/ExportSettings';
import { getReportExportStorageKey } from './ReportPrintPage';
import { resolveLanguageCode } from '../utils/locale';
import { STORAGE_KEYS, readStorageValue } from '../utils/storageKeys';
import './ReportPreviewPage.css';

const OFFICIAL_EXPORT_PROFILE = 'official_v1';
const PREVIEW_EXPORT_PROFILE = OFFICIAL_EXPORT_PROFILE;
const PDF_EXPORT_ENDPOINT_PATH = '/api/reports/export/pdf';

const isPdfExportNetworkError = (error) => {
  if (!error || error?.name === 'AbortError') {
    return false;
  }
  if (error instanceof TypeError) {
    return true;
  }
  const message = String(error?.message || '');
  return /ERR_CONNECTION_REFUSED|Failed to fetch|NetworkError|Load failed/i.test(message);
};

const getPdfExportServiceTarget = () => {
  const backendOrigin = getBackendOrigin();
  if (backendOrigin) {
    return `${backendOrigin}${PDF_EXPORT_ENDPOINT_PATH}`;
  }
  if (typeof window !== 'undefined' && typeof window.location?.origin === 'string') {
    return `${window.location.origin}${PDF_EXPORT_ENDPOINT_PATH}`;
  }
  return PDF_EXPORT_ENDPOINT_PATH;
};

const readSessionPayload = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const sessionEntry = readStorageValue(window.localStorage, STORAGE_KEYS.session);
    return sessionEntry?.value ? JSON.parse(sessionEntry.value) : null;
  } catch (error) {
    return null;
  }
};

const getFirstCoverBlock = (templateDocument) => (
  (templateDocument?.sections || [])
    .flatMap(section => section?.blocks || [])
    .find(block => block?.type === 'cover') || null
);

const getTocHeadingTitle = (templateDocument, locale) => {
  const tocSection = (templateDocument?.sections || []).find(section => section?.id === 'toc');
  if (tocSection?.title) {
    return tocSection.title;
  }
  if (locale === 'es') {
    return 'Índice';
  }
  if (locale === 'en') {
    return 'Contents';
  }
  return 'Indice';
};

const computeChecksum = (value) => {
  const source = String(value || '');
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) {
    hash = ((hash << 5) - hash) + source.charCodeAt(index);
    hash |= 0;
  }
  return (hash >>> 0).toString(36);
};

export function ReportPreviewFlow({
  report: reportProp,
  isEmbedded = false,
  onBack,
  onOpenExport,
  onCloseExport,
  isExportStep = false,
  onExportComplete,
}) {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { navigateTo, getLocalizedPath } = useLanguageNavigation();
  const { toast } = useToast();
  const previewTitle = isExportStep
    ? t('reports.workspace.stepper.export')
    : t('reports.workspace.stepper.preview');
  const uiLocale = resolveLanguageCode(i18n.language);

  const [report, setReport] = useState(reportProp || null);
  const [loading, setLoading] = useState(!reportProp);
  const [isExportModalOpen, setIsExportModalOpen] = useState(isExportStep);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewRefreshNonce, setPreviewRefreshNonce] = useState(0);
  const replacePreviewPdfUrl = useCallback((nextUrl) => {
    setPreviewPdfUrl((previousUrl) => {
      if (previousUrl && previousUrl !== nextUrl) {
        URL.revokeObjectURL(previousUrl);
      }
      return nextUrl;
    });
  }, []);

  const reportId = report?.id || id;
  const storageKey = useMemo(
    () => getDraftStorageKey(reportId, uiLocale),
    [reportId, uiLocale]
  );
  const [storedDraft, setStoredDraft] = useState(null);
  const [isDraftLoadResolved, setIsDraftLoadResolved] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    setIsDraftLoadResolved(false);
    (async () => {
      let nextDraft = null;
      try {
        nextDraft = await loadBestDraft({ reportId, locale: uiLocale, storageKey });
      } catch (error) {
        nextDraft = null;
      }
      if (isCancelled) {
        return;
      }
      setStoredDraft(nextDraft);
      setIsDraftLoadResolved(true);
    })();
    return () => {
      isCancelled = true;
    };
  }, [reportId, storageKey, uiLocale]);

  useEffect(() => {
    if (reportProp) {
      setReport(reportProp);
      setLoading(false);
      return;
    }
    if (!id) {
      setLoading(false);
      return;
    }

    const loadReport = async () => {
      setLoading(true);
      const reportData = getReportById(id);

      if (!reportData) {
        toast({
          title: t('reports.workspace.notFound'),
          description: t('reports.previewPage.notFoundDescription'),
          variant: 'destructive',
        });
        navigateTo('/reports', { replace: true });
        return;
      }

      setReport(reportData);
      setLoading(false);
    };

    loadReport();
  }, [id, navigateTo, reportProp, t, toast]);

  useEffect(() => {
    if (!isEmbedded) {
      return;
    }
    setIsExportModalOpen(isExportStep);
  }, [isEmbedded, isExportStep]);

  const dossier = useMemo(
    () => (report ? getDossierByReportId(report.id, report.type) : null),
    [report]
  );
  const templateT = useMemo(() => i18n.getFixedT(uiLocale), [i18n, uiLocale]);
  const templatePayload = useMemo(() => {
    if (!report) {
      return null;
    }
    return buildReportTemplateDocument({
      report,
      reportType: report?.type,
      locale: uiLocale,
      t: templateT,
      dossier,
      stations: MOCK_STATIONS,
    });
  }, [report, report?.type, templateT, dossier, uiLocale]);
  const templateDocument = templatePayload?.document || null;
  const templateCoverBlock = useMemo(
    () => getFirstCoverBlock(templateDocument),
    [templateDocument]
  );
  const tocHeadingTitle = useMemo(
    () => getTocHeadingTitle(templateDocument, uiLocale),
    [templateDocument, uiLocale]
  );

  const rawSerializedState = useMemo(
    () => (isDraftLoadResolved ? getSerializedEditorState(storedDraft) : null),
    [isDraftLoadResolved, storedDraft]
  );
  const serializedState = useMemo(() => {
    if (!rawSerializedState) {
      return rawSerializedState;
    }
    return sanitizeSerializedStateForOfficialExport(rawSerializedState, uiLocale, {
      maxTocLevel: 2,
      removeInternalSections: true,
      normalizeStructure: true,
      tocHeadingTitle,
      templateCoverBlock,
      templateDocument,
    });
  }, [rawSerializedState, tocHeadingTitle, templateCoverBlock, templateDocument, uiLocale]);

  const templateChecksum = useMemo(() => {
    try {
      return computeChecksum(JSON.stringify(templateDocument || null));
    } catch (error) {
      return '0';
    }
  }, [templateDocument]);
  const serializedStateChecksum = useMemo(
    () => computeChecksum(serializedState || ''),
    [serializedState]
  );
  const previewGenerationKey = useMemo(
    () => [
      report?.id || 'no-report',
      templateChecksum,
      serializedStateChecksum,
      previewRefreshNonce,
    ].join(':'),
    [report?.id, templateChecksum, serializedStateChecksum, previewRefreshNonce]
  );

  const buildPdfRequestPayload = useCallback((fileName) => {
    if (!report || !templateDocument) {
      return null;
    }

    const exportStorageKey = getReportExportStorageKey(report.id);
    const exportPayload = {
      reportPayload: report,
      templateDocument,
      serializedState,
      exportProfile: PREVIEW_EXPORT_PROFILE,
      templateVersion: templateDocument?.version || null,
    };

    if (typeof window !== 'undefined' && exportStorageKey) {
      try {
        window.localStorage.setItem(exportStorageKey, JSON.stringify(exportPayload));
      } catch (error) {
        // Ignore localStorage write failures and rely on backend payload injection.
      }
    }

    return {
      reportId: report.id,
      lang: uiLocale,
      printPath: `/${uiLocale}/reports/${report.id}/print`,
      storageKey,
      draftPayload: storedDraft || null,
      sessionPayload: readSessionPayload(),
      fileName,
      reportPayload: report,
      templateDocument,
      serializedState,
      exportStorageKey,
      exportProfile: PREVIEW_EXPORT_PROFILE,
      templateVersion: templateDocument?.version || null,
    };
  }, [report, templateDocument, serializedState, storageKey, storedDraft, uiLocale]);

  const requestPdfBlob = useCallback(async ({ fileName, signal }) => {
    const payload = buildPdfRequestPayload(fileName);
    if (!payload) {
      throw new Error(t('reports.previewPage.notFoundDescription'));
    }

    let response;
    try {
      response = await fetch(getBackendEndpoint('/reports/export/pdf'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal,
      });
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw error;
      }
      if (isPdfExportNetworkError(error)) {
        const serviceTarget = getPdfExportServiceTarget();
        throw new Error(
          serviceTarget
            ? t('reports.previewPage.errors.pdfServiceUnavailableWithHost', {
              host: serviceTarget,
              defaultValue:
                'PDF preview service is unavailable at {{host}}. Check backend URL/configuration and ensure the backend is running.',
            })
            : t('reports.previewPage.errors.pdfServiceUnavailable', {
              defaultValue:
                'PDF preview service is unavailable. Check backend URL/configuration and ensure the backend is running.',
            })
        );
      }
      throw error;
    }

    if (!response.ok) {
      let errorMessage = t('reports.previewPage.notFoundDescription');
      try {
        const errorPayload = await response.json();
        errorMessage = errorPayload?.message || errorMessage;
      } catch (error) {
        // Keep default message when response JSON is unavailable.
      }
      throw new Error(errorMessage);
    }

    return response.blob();
  }, [buildPdfRequestPayload, t]);

  useEffect(() => {
    if (!report?.id || !templateDocument) {
      setIsPreviewLoading(false);
      setPreviewError('');
      replacePreviewPdfUrl(null);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    setIsPreviewLoading(true);
    setPreviewError('');

    requestPdfBlob({
      fileName: `report-${report.id}-preview.pdf`,
      signal: controller.signal,
    })
      .then((blob) => {
        if (cancelled) {
          return;
        }
        const nextUrl = URL.createObjectURL(blob);
        replacePreviewPdfUrl(nextUrl);
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') {
          return;
        }
        setPreviewError(error?.message || t('reports.previewPage.notFoundDescription'));
      })
      .finally(() => {
        if (!cancelled) {
          setIsPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [report?.id, templateDocument, previewGenerationKey, requestPdfBlob, replacePreviewPdfUrl, t]);

  useEffect(() => () => {
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
    }
  }, [previewPdfUrl]);

  const handleRefreshPreview = useCallback(() => {
    setPreviewRefreshNonce(previous => previous + 1);
  }, []);

  const handleOpenExport = () => {
    if (typeof onOpenExport === 'function') {
      onOpenExport();
    }
    setIsExportModalOpen(true);
  };

  const closeExportModal = (shouldNotify = true) => {
    setIsExportModalOpen(false);
    if (shouldNotify && typeof onCloseExport === 'function') {
      onCloseExport();
    }
  };

  const handleCloseExport = () => {
    closeExportModal(true);
  };

  const handleExport = async (format) => {
    if (!report) {
      return;
    }
    try {
      let blob;
      let extension = 'pdf';

      if (format === 'PDF') {
        blob = await requestPdfBlob({
          fileName: `report-${report.id}.pdf`,
        });
        extension = 'pdf';
      } else {
        const nonPdfTemplatePayload = buildReportTemplateDocument({
          report,
          reportType: report?.type,
          locale: uiLocale,
          t: i18n.getFixedT(uiLocale),
          dossier,
          stations: MOCK_STATIONS,
        });
        const exportedContent = format === 'LATEX'
          ? (nonPdfTemplatePayload?.latex || nonPdfTemplatePayload?.text || '')
          : 'Mock exported content';
        const mimeType = format === 'DOCX'
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : 'text/x-tex';
        extension = format === 'DOCX' ? 'docx' : 'tex';
        blob = new Blob([exportedContent], {
          type: mimeType,
        });
      }

      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = `report-${report.id}.${extension}`;
      downloadLink.click();
      URL.revokeObjectURL(url);

      const updatedReport = {
        ...report,
        status: 'completed',
        progress: 100,
        lastModified: new Date(),
      };
      setReport(updatedReport);
      addMockReport(updatedReport);
      if (typeof onExportComplete === 'function') {
        onExportComplete(updatedReport);
      }

      toast({
        title: t('reports.previewPage.exportedTitle'),
        description: t('reports.previewPage.exportedAs', { format }),
      });
      closeExportModal(false);
      if (!isEmbedded) {
        navigateTo('/reports');
      }
    } catch (error) {
      toast({
        title: t('reports.workspace.toast.genericError.title', { defaultValue: 'Export failed' }),
        description: error?.message || t('reports.workspace.toast.genericError.description', {
          defaultValue: 'An unexpected error occurred while exporting the report.',
        }),
        variant: 'destructive',
      });
    }
  };

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    if (id) {
      navigateTo(`/reports/${id}`);
    }
  };

  const previewFrame = (
    <div className="report-preview-frame">
      {isPreviewLoading ? (
        <div className="report-preview-frame__status">{t('common.loading')}</div>
      ) : null}
      {!isPreviewLoading && previewPdfUrl ? (
        <iframe
          key={previewGenerationKey}
          src={previewPdfUrl}
          title={t('reports.preview.title')}
          className="report-preview-frame__iframe"
        />
      ) : null}
      {!isPreviewLoading && previewError ? (
        <div className="report-preview-frame__error">
          <p>{previewError}</p>
          <Button
            variant="secondary"
            size="sm"
            className="report-preview-frame__retry"
            onClick={handleRefreshPreview}
          >
            {t('common.retry', { defaultValue: 'Retry' })}
          </Button>
        </div>
      ) : null}
      {!isPreviewLoading && !previewError && !previewPdfUrl ? (
        <div className="report-preview-frame__empty">{t('common.loading')}</div>
      ) : null}
    </div>
  );

  if (loading) {
    return (
      <div className={isEmbedded ? 'report-preview-flow report-preview-flow--embedded' : 'report-preview-page'}>
        <div className="report-preview-page__loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  if (isEmbedded) {
    return (
      <div className="report-preview-flow report-preview-flow--embedded evidence-stepper-flow">
        <div className="evidence-stepper-flow__section">
          <div className="evidence-stepper-flow__block report-preview-flow__header-block">
            <div className="report-preview-flow__header">
              <div className="report-preview-flow__header-copy">
                <h2 className="report-preview-flow__title">
                  {previewTitle}
                </h2>
                <p className="report-preview-flow__subtitle">
                  {t('reports.previewPage.subtitle')}
                </p>
              </div>
            </div>
          </div>

          <div className="evidence-stepper-flow__block report-preview-flow__preview-block">
            {previewFrame}
          </div>

          <div className="evidence-stepper-flow__actions report-preview-flow__actions">
            <div className="report-preview-flow__actions-left">
              <Button
                variant="secondary"
                onClick={handleBack}
              >
                {t('reports.previewPage.backToDrafting')}
              </Button>
            </div>
            <div className="report-preview-flow__actions-right">
              <Button
                variant="secondary"
                onClick={handleRefreshPreview}
                disabled={isPreviewLoading}
              >
                {t('common.refresh', { defaultValue: 'Refresh' })}
              </Button>
              <Button
                variant="primary"
                icon={<Download size={18} />}
                onClick={handleOpenExport}
              >
                {t('common.export')}
              </Button>
            </div>
          </div>
        </div>

        <ExportSettings
          isOpen={isExportModalOpen}
          onClose={handleCloseExport}
          onExport={handleExport}
          report={report}
        />
      </div>
    );
  }

  return (
    <div className="report-preview-page">
      {/* Header */}
      <div className="report-preview-page__header">
        <div className="report-preview-page__header-left">
          <nav className="report-preview-page__breadcrumb">
            <Link to={getLocalizedPath('/reports')} className="report-preview-page__breadcrumb-link">
              {t('reports.title')}
            </Link>
            <span className="report-preview-page__breadcrumb-separator">/</span>
            <Link to={getLocalizedPath(`/reports/${id}`)} className="report-preview-page__breadcrumb-link">
              {report.name}
            </Link>
            <span className="report-preview-page__breadcrumb-separator">/</span>
            <span className="report-preview-page__breadcrumb-current">{t('reports.preview.title')}</span>
          </nav>
          <h1 className="report-preview-page__title">{report.name}</h1>
        </div>
        <div className="report-preview-page__header-right">
          <Button
            variant="secondary"
            size="md"
            onClick={handleRefreshPreview}
            disabled={isPreviewLoading}
          >
            {t('common.refresh', { defaultValue: 'Refresh' })}
          </Button>
          <Button
            variant="secondary"
            size="md"
            icon={<ArrowLeft size={16} />}
            onClick={handleBack}
          >
            {t('reports.previewPage.backToEdit')}
          </Button>
        </div>
      </div>

      {/* Preview Content */}
      <div className="report-preview-page__content">
        {previewFrame}
      </div>

      {/* Action Bar */}
      <div className="report-preview-page__action-bar">
        <div className="report-preview-page__action-bar-left">
          <Button
            variant="secondary"
            size="md"
            icon={<ArrowLeft size={18} />}
            onClick={handleBack}
          >
            {t('reports.previewPage.backToEdit')}
          </Button>
        </div>
        <div className="report-preview-page__action-bar-right">
          <Button
            variant="secondary"
            size="md"
            onClick={handleRefreshPreview}
            disabled={isPreviewLoading}
          >
            {t('common.refresh', { defaultValue: 'Refresh' })}
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={<Download size={18} />}
            onClick={handleOpenExport}
          >
            {t('common.export')}
          </Button>
        </div>
      </div>

      {/* Export Modal */}
      <ExportSettings
        isOpen={isExportModalOpen}
        onClose={handleCloseExport}
        onExport={handleExport}
        report={report}
      />
    </div>
  );
}

export function ReportPreviewPage() {
  return <ReportPreviewFlow />;
}

export default ReportPreviewPage;
